import { useEffect, useMemo, useRef, useState } from 'react';
import type { AgreementStatus, AgreementType } from '@catalyst-psa/shared';
import {
  AGREEMENT_STATUSES,
  AGREEMENT_TYPES,
  AGREEMENT_TYPE_LABELS,
  DEFAULT_PAGE_SIZE,
} from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { DetailOverlay } from '../../components/layout/DetailOverlay.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { formatDate } from '../../utils/date.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { useAgreementSubtypeOptions } from '../agreement-subtypes/api.js';
import { AgreementForecastPanel } from '../forecast/AgreementForecastPanel.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import { savedString, savedStringArray, useFilterDefaults } from '../filters/useFilterDefaults.js';
import { AgreementFormModal } from './AgreementFormModal.js';
import { useAgreements } from './api.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { AGREEMENT_STATUS_LABELS, AGREEMENT_STATUS_TONES } from './status.js';

interface FilterOption {
  value: string;
  label: string;
}

/**
 * Compact multi-select filter: a dropdown button revealing a checkbox list. Empty
 * selection = no filtering (the "All …" label).
 */
function MultiFilterSelect({
  options,
  selected,
  allLabel,
  onChange,
}: {
  options: FilterOption[];
  selected: string[];
  allLabel: string;
  onChange: (next: string[]) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const labelOf = (value: string): string =>
    options.find((option) => option.value === value)?.label ?? value;

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length <= 2
        ? selected.map(labelOf).join(', ')
        : `${selected.length} selected`;

  const toggle = (value: string, checked: boolean): void => {
    onChange(checked ? [...selected, value] : selected.filter((v) => v !== value));
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex min-w-[12rem] items-center justify-between gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      >
        <span className="truncate">{summary}</span>
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open ? (
        <div className="absolute z-20 mt-1 max-h-72 w-64 overflow-auto rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          <button
            type="button"
            onClick={() => onChange([])}
            className="mb-1 w-full rounded-md border-b border-slate-100 px-2 py-1.5 text-left text-xs font-medium text-slate-500 hover:bg-slate-50"
          >
            Clear ({allLabel.toLowerCase()})
          </button>
          {options.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={(e) => toggle(option.value, e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function AgreementsPage(): JSX.Element {
  const { can, canSeeMenu } = useAuth();
  const canEdit = can('agreements', 'edit');
  const rowLink = useRowLink();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<AgreementStatus | ''>('');
  const [type, setType] = useState<AgreementType | ''>('');
  const [customerId, setCustomerId] = useState('');
  const [subtypeIds, setSubtypeIds] = useState<string[]>([]);

  const [formOpen, setFormOpen] = useState(false);

  const filterDefaults = useFilterDefaults('agreements', (saved) => {
    const savedStatus = savedString(saved, 'status');
    if (savedStatus !== null) setStatus(savedStatus as AgreementStatus | '');
    const savedType = savedString(saved, 'type');
    if (savedType !== null) setType(savedType as AgreementType | '');
    const savedCustomer = savedString(saved, 'customerId');
    if (savedCustomer !== null) setCustomerId(savedCustomer);
    const savedSubtypes = savedStringArray(saved, 'subtypeIds');
    if (savedSubtypes !== null) setSubtypeIds(savedSubtypes);
  });

  const subtypesQuery = useAgreementSubtypeOptions();
  const subtypeOptions = useMemo<FilterOption[]>(
    () => (subtypesQuery.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    [subtypesQuery.data],
  );

  const query = useAgreements({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    status: status || undefined,
    type: type || undefined,
    customerId: customerId || undefined,
    subtypeIds: subtypeIds.length ? subtypeIds : undefined,
  });

  const agreements = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const handleStatusChange = (value: string): void => {
    setPage(1);
    setStatus(value as AgreementStatus | '');
  };

  const handleTypeChange = (value: string): void => {
    setPage(1);
    setType(value as AgreementType | '');
  };

  const handleCustomerChange = (value: string): void => {
    setPage(1);
    setCustomerId(value);
  };

  const handleSubtypesChange = (next: string[]): void => {
    setPage(1);
    setSubtypeIds(next);
  };

  const hasFilters = Boolean(status || type || customerId || subtypeIds.length);

  return (
    <div>
      <PageHeader
        title="Agreements"
        description="Recurring billing arrangements: blocks of hours, fixed fees, and time & materials."
        actions={
          canEdit ? (
            <Button onClick={() => setFormOpen(true)}>
              <Icon name="agreements" className="h-4 w-4" />
              New agreement
            </Button>
          ) : undefined
        }
      />

      {canSeeMenu('forecast') ? (
        <AgreementForecastPanel enabled={can('agreements')} defaultCollapsed />
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
          <div className="w-64">
            <CustomerSelectField
              value={customerId}
              onChange={handleCustomerChange}
              placeholder="All customers"
              allowClear
            />
          </div>
          <select
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All statuses</option>
            {AGREEMENT_STATUSES.map((value) => (
              <option key={value} value={value}>
                {AGREEMENT_STATUS_LABELS[value]}
              </option>
            ))}
          </select>
          <select
            value={type}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All types</option>
            {AGREEMENT_TYPES.map((value) => (
              <option key={value} value={value}>
                {AGREEMENT_TYPE_LABELS[value]}
              </option>
            ))}
          </select>
          <MultiFilterSelect
            options={subtypeOptions}
            selected={subtypeIds}
            allLabel="All subtypes"
            onChange={handleSubtypesChange}
          />
          <FilterDefaultsButtons
            handle={filterDefaults}
            onSave={() => filterDefaults.save({ status, type, customerId, subtypeIds })}
          />
        </div>

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Subtype</th>
              <th className="px-4 py-3 font-medium">Start</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={6}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={agreements.length === 0}
              emptyTitle="No agreements found"
              emptyDescription={
                hasFilters
                  ? 'Try a different filter.'
                  : 'Create your first agreement to get started.'
              }
              emptyAction={
                canEdit && !hasFilters ? (
                  <Button onClick={() => setFormOpen(true)}>New agreement</Button>
                ) : undefined
              }
            >
              {agreements.map((agreement) => (
                <tr
                  key={agreement.id}
                  className="cursor-pointer hover:bg-slate-50"
                  {...rowLink(`/agreements/${agreement.id}`)}
                >
                  <td className="px-4 py-3 text-slate-600">{agreement.customerName ?? '—'}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{agreement.name}</td>
                  <td className="px-4 py-3">
                    <Badge tone={AGREEMENT_STATUS_TONES[agreement.status]}>
                      {AGREEMENT_STATUS_LABELS[agreement.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {AGREEMENT_TYPE_LABELS[agreement.type]}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{agreement.subtypeName ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(agreement.startDate)}</td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && agreements.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <AgreementFormModal isOpen={formOpen} agreement={null} onClose={() => setFormOpen(false)} />
      <DetailOverlay listPath="/agreements" />
    </div>
  );
}
